import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'
import { calcItemsListProps } from '@aytm/app/stats/page/question-views/info-view/components/charts/quadrant/lib/items-list'

import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'
import Chart from '@aytm/app/stats/page/question-views/info-view/components/charts/quadrant/Chart'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

// First paint, before the report column can be read. Matches the chart's own default.
const DEFAULT_WIDTH = 814

// Info-view quadrant block: the quadrant chart plus its items-list table. Replaces
// info_view/charts/quadrant.coffee (QuadrantChartView + QuadrantItemsListView + QuadrantView).
// onChartReady is passed through to Chart so the coffee shell can keep the pptx formatSVG bridge.
const Block = ({
  id,
  questionKey,
  format,
  answer,
  batches,
  questionVariants,
  xAxisLimits,
  yAxisLimits,
  colorTheme,
  chartLegendMode,
  features,
  table,
  showTable,
  isAnimated,
  onChartReady
}) => {
  // The quadrant is drawn square, so the placeholder reserves the same box the chart will take --
  // otherwise the page jumps on reveal. `required` keeps the export able to force the block open.
  const { width } = useComponentWidth({ defaultWidth: DEFAULT_WIDTH })

  const itemsListProps = useMemo(
    () => (showTable && table ? calcItemsListProps({ id, table, answer, questionVariants, chartLegendMode }) : null),
    [showTable, table, id, answer, questionVariants, chartLegendMode]
  )

  return (
    <BlockWrapper>
      <ChartPlaceholder height={width} required isLoading={'loading' === answer} isAnimated={isAnimated}>
        <Chart
          questionId={id}
          questionKey={questionKey}
          format={format}
          answer={answer}
          batches={batches}
          questionVariants={questionVariants}
          xAxisLimits={xAxisLimits}
          yAxisLimits={yAxisLimits}
          colorTheme={colorTheme}
          chartLegendMode={chartLegendMode}
          features={features}
          onChartReady={onChartReady}
        />
      </ChartPlaceholder>
      {itemsListProps ? <Table {...itemsListProps} /> : null}
    </BlockWrapper>
  )
}

Block.displayName = 'Block'

Block.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  format: PropTypes.object,
  answer: answerPropType,
  batches: PropTypes.array.isRequired,
  questionVariants: PropTypes.array.isRequired,
  xAxisLimits: PropTypes.array,
  yAxisLimits: PropTypes.array,
  colorTheme: PropTypes.array.isRequired,
  chartLegendMode: PropTypes.string,
  features: PropTypes.object,
  table: PropTypes.array,
  showTable: PropTypes.bool,
  isAnimated: PropTypes.bool,
  onChartReady: PropTypes.func
}

export default Block
